import { useState, useRef, useEffect } from 'react';
import type { RaciPerson, RaciUser } from '../../types/raci';

interface RaciPersonPickerProps {
  users: RaciUser[];
  selected: RaciPerson[];
  onChange: (people: RaciPerson[]) => void;
  multiple: boolean;
  disabled?: boolean;
  label: string;
}

export function RaciPersonPicker({
  users,
  selected,
  onChange,
  multiple,
  disabled = false,
  label,
}: RaciPersonPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchLower = search.toLowerCase().trim();

  const filteredUsers = users.filter((u) => {
    const fullName = `${u.firstName} ${u.lastName}`.toLowerCase();
    return !searchLower || fullName.includes(searchLower) || u.email.toLowerCase().includes(searchLower);
  });

  // Build a set of selected user IDs (for account-based entries only)
  const selectedUserIds = new Set(
    selected.filter((p) => p.userId && !p.freeText).map((p) => p.userId),
  );

  // Build a set of selected free-text names (case-insensitive)
  const selectedFreeTextNames = new Set(
    selected.filter((p) => p.freeText).map((p) => p.name.toLowerCase()),
  );

  // Check if the search term matches an existing free-text entry exactly
  const searchMatchesFreeText = searchLower && selectedFreeTextNames.has(searchLower);

  // Check if the search term matches an existing user entry exactly
  const searchMatchesUser = searchLower && filteredUsers.some(
    (u) => `${u.firstName} ${u.lastName}`.toLowerCase() === searchLower && selectedUserIds.has(u.id),
  );

  // Show "Add as free text" option when there's typed text that isn't already assigned
  const showFreeTextOption = searchLower && !searchMatchesFreeText && !searchMatchesUser;

  function handleToggleUser(user: RaciUser) {
    const person: RaciPerson = {
      userId: user.id,
      name: `${user.firstName} ${user.lastName}`,
    };

    if (multiple) {
      if (selectedUserIds.has(user.id)) {
        onChange(selected.filter((p) => !(p.userId === user.id && !p.freeText)));
      } else {
        onChange([...selected, person]);
      }
    } else {
      // Single select — replace or clear
      if (selectedUserIds.has(user.id)) {
        onChange([]);
      } else {
        onChange([person]);
      }
      setOpen(false);
      setSearch('');
    }
  }

  function handleAddFreeText(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;

    // Don't add if already exists as free-text
    if (selectedFreeTextNames.has(trimmed.toLowerCase())) return;

    const person: RaciPerson = {
      userId: '',
      name: trimmed,
      freeText: true,
    };

    if (multiple) {
      onChange([...selected, person]);
    } else {
      onChange([person]);
      setOpen(false);
    }
    setSearch('');
    // Re-focus input for quick sequential adds
    inputRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = search.trim();
      if (!trimmed) return;

      // If there's exactly one matching user, toggle them
      if (filteredUsers.length === 1 && !showFreeTextOption) {
        handleToggleUser(filteredUsers[0]);
        setSearch('');
        return;
      }

      // Otherwise add as free text
      if (showFreeTextOption) {
        handleAddFreeText(trimmed);
      }
    }
  }

  function handleRemove(index: number) {
    if (disabled) return;
    onChange(selected.filter((_, i) => i !== index));
  }

  // Generate a unique key for each selected person
  function getPersonKey(person: RaciPerson, index: number): string {
    if (person.freeText) return `freetext-${index}-${person.name}`;
    return `user-${person.userId}`;
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Selected people tags + add button */}
      <div className="flex flex-wrap gap-1 min-h-[32px] items-center">
        {selected.map((person, index) => (
          <span
            key={getPersonKey(person, index)}
            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
              person.freeText
                ? 'bg-gray-100 text-gray-700 border border-gray-300'
                : 'bg-purple-100 text-purple-800'
            }`}
            title={person.freeText ? 'Free text entry' : `User account: ${person.name}`}
          >
            {person.freeText && (
              <svg className="w-3 h-3 flex-shrink-0 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            )}
            <span className="max-w-[100px] truncate">{person.name}</span>
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className={`ml-0.5 ${person.freeText ? 'text-gray-400 hover:text-gray-600' : 'text-purple-500 hover:text-purple-700'}`}
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
            title={`Assign ${label}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
          {/* Search / free-text input */}
          <div className="px-2 pb-1 pt-1">
            <input
              ref={inputRef}
              type="text"
              placeholder="Search or type a name/role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-purple-500 focus:border-purple-500 outline-none"
              autoFocus
            />
          </div>

          <div className="max-h-52 overflow-y-auto">
            {/* Free text add option */}
            {showFreeTextOption && (
              <button
                type="button"
                onClick={() => handleAddFreeText(search)}
                className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-teal-50 transition-colors flex items-center gap-2 border-b border-gray-100"
              >
                <svg className="w-4 h-4 text-teal-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <span>
                  Add <strong className="text-teal-700">"{search.trim()}"</strong> as free text
                </span>
              </button>
            )}

            {/* User accounts section */}
            {filteredUsers.length > 0 && (
              <>
                {showFreeTextOption && (
                  <p className="px-3 pt-2 pb-1 text-xs font-medium text-gray-400 uppercase tracking-wide">
                    User Accounts
                  </p>
                )}
                {filteredUsers.map((user) => {
                  const isSelected = selectedUserIds.has(user.id);
                  return (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => handleToggleUser(user)}
                      className={`w-full text-left px-3 py-1.5 text-sm transition-colors flex items-center justify-between ${
                        isSelected
                          ? 'bg-purple-50 text-purple-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{user.email}</p>
                      </div>
                      {isSelected && (
                        <svg
                          className="w-4 h-4 text-purple-600 flex-shrink-0 ml-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </>
            )}

            {/* Empty state */}
            {filteredUsers.length === 0 && !showFreeTextOption && (
              <p className="text-xs text-gray-400 px-3 py-2">No matches found</p>
            )}
          </div>

          {/* Help text */}
          <div className="px-3 py-1.5 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              Type a name, role, or department and press Enter to add free text
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
